import React, { useState } from 'react';
import { BookOpen, Calendar, CheckCircle, Clock, FileText, Home, Award, TrendingUp, User, AlertCircle, Send, Eye, Upload } from 'lucide-react';
import 'bootstrap/dist/css/bootstrap.min.css';

// DEMO DATA
const DEMO_DATA = {
  currentStudent: {
    id: 'stud_001',
    firstName: 'Oliver',
    lastName: 'Wright',
    email: 'oliver.wright@brischool.edu',
    yearGroup: 'Year 7',
    studentNumber: 'BS2024-001',
    house: 'Phoenix'
  },

  academicSummary: {
    overallGrade: 85.2,
    attendance: 95.0,
    creditsEarned: 24,
    creditsRequired: 30,
    rank: 5,
    totalStudents: 125
  },

  classes: [
    { id: 'cls_001', name: 'English', teacher: 'Sarah Mitchell', room: '204', schedule: 'Mon/Wed/Fri 9:00-10:00', currentGrade: 85.2 },
    { id: 'cls_002', name: 'Mathematics', teacher: 'James Chen', room: '301', schedule: 'Mon/Wed/Fri 10:15-11:15', currentGrade: 82.0 },
    { id: 'cls_003', name: 'Science', teacher: 'Aisha Patel', room: '105', schedule: 'Tue/Thu 9:00-10:30', currentGrade: 88.5 },
    { id: 'cls_004', name: 'History', teacher: 'Michael Brown', room: '210', schedule: 'Tue/Thu 11:00-12:00', currentGrade: 80.0 },
    { id: 'cls_005', name: 'Physical Education', teacher: 'Emma Wilson', room: 'Gym', schedule: 'Mon/Wed 1:00-2:00', currentGrade: 92.0 }
  ],

  assessments: [
    {
      id: 'assess_001',
      title: 'Romeo and Juliet Essay',
      className: 'English',
      dueDate: '2024-12-30',
      status: 'submitted',
      submittedAt: '2024-12-28T14:30:00',
      totalPoints: 100,
      score: 85,
      feedback: 'Excellent analysis of the theme of fate! Your use of quotations is strong and your argument is well-structured.',
      gradedAt: '2024-12-29T16:00:00'
    },
    {
      id: 'assess_002',
      title: 'Algebra Problem Set 5',
      className: 'Mathematics',
      dueDate: '2025-01-05',
      status: 'available',
      totalPoints: 50,
      description: 'Complete problems 1-20 from Chapter 5. Show all work.',
      questions: [
        { id: 'q1', text: 'Solve for x: 2x + 5 = 15', type: 'short_answer' },
        { id: 'q2', text: 'Factor: x² + 5x + 6', type: 'short_answer' },
        { id: 'q3', text: 'Graph the equation: y = 2x - 3', type: 'file_upload' }
      ]
    },
    {
      id: 'assess_003',
      title: 'Chemistry Lab Report',
      className: 'Science',
      dueDate: '2025-01-08',
      status: 'available',
      totalPoints: 75,
      description: 'Write a lab report on the acid-base titration experiment conducted in class.',
      questions: [
        { id: 'q1', text: 'Describe the procedure', type: 'essay' },
        { id: 'q2', text: 'Record your observations', type: 'essay' },
        { id: 'q3', text: 'Analyze the results', type: 'essay' }
      ]
    }
  ],

  upcomingEvents: [
    { id: 'evt_001', title: 'Math Quiz', date: '2025-01-10', time: '10:15 AM', location: 'Room 301', type: 'assessment' },
    { id: 'evt_002', title: 'Science Fair', date: '2025-01-15', time: '1:00 PM', location: 'Auditorium', type: 'event' },
    { id: 'evt_003', title: 'Parent-Teacher Conference', date: '2025-01-20', time: '3:00 PM', location: 'Main Office', type: 'meeting' }
  ],

  recentGrades: [
    { assessment: 'Romeo and Juliet Essay', class: 'English', score: 85, maxScore: 100, date: '2024-12-29' },
    { assessment: 'Chapter 4 Test', class: 'Mathematics', score: 42, maxScore: 50, date: '2024-12-20' },
    { assessment: 'Lab Report - Photosynthesis', class: 'Science', score: 68, maxScore: 75, date: '2024-12-15' }
  ]
};

const BriSchoolStudentBootstrap = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [workingOnAssessment, setWorkingOnAssessment] = useState(null);
  const [viewingGrade, setViewingGrade] = useState(null);
  const [answers, setAnswers] = useState({});

  const handleAnswerChange = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const submitAssessment = () => {
    alert('Assessment submitted successfully!');
    setWorkingOnAssessment(null);
    setAnswers({});
  };

  const getGradeColor = (score, maxScore) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 85) return 'text-success';
    if (percentage >= 75) return 'text-info';
    if (percentage >= 65) return 'text-warning';
    return 'text-danger';
  };

  const getStatusBadge = (status) => {
    if (status === 'submitted') return 'badge bg-warning';
    if (status === 'graded') return 'badge bg-success';
    if (status === 'available') return 'badge bg-primary';
    return 'badge bg-secondary';
  };

  // Viewing Grade Details
  if (viewingGrade) {
    const assessment = DEMO_DATA.assessments.find(a => a.id === viewingGrade);
    const percentage = ((assessment.score / assessment.totalPoints) * 100).toFixed(0);

    return (
      <div className="min-vh-100 bg-light">
        <nav className="navbar navbar-light bg-white border-bottom shadow-sm">
          <div className="container-fluid">
            <button onClick={() => setViewingGrade(null)} className="btn btn-link text-decoration-none">
              ← Back
            </button>
            <span className="navbar-brand mb-0 h1">Assessment Results</span>
          </div>
        </nav>

        <div className="container py-4">
          <div className="card text-white bg-success shadow mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="mb-1 opacity-75">Your Score</p>
                  <h1 className="display-3 mb-0">{assessment.score}/{assessment.totalPoints}</h1>
                  <p className="fs-4 mb-0">{percentage}%</p>
                </div>
                <Award size={80} className="opacity-25" />
              </div>
            </div>
          </div>

          <div className="card shadow mb-4">
            <div className="card-body">
              <h5 className="card-title">{assessment.title}</h5>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <small className="text-muted">Class:</small>
                  <p className="fw-bold mb-0">{assessment.className}</p>
                </div>
                <div className="col-md-6 mb-3">
                  <small className="text-muted">Submitted:</small>
                  <p className="fw-bold mb-0">{new Date(assessment.submittedAt).toLocaleString()}</p>
                </div>
                <div className="col-md-6 mb-3">
                  <small className="text-muted">Graded:</small>
                  <p className="fw-bold mb-0">{new Date(assessment.gradedAt).toLocaleString()}</p>
                </div>
                <div className="col-md-6 mb-3">
                  <small className="text-muted">Grade:</small>
                  <p className={`fw-bold fs-4 mb-0 ${getGradeColor(assessment.score, assessment.totalPoints)}`}>
                    {percentage}%
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="card shadow">
            <div className="card-body">
              <h5 className="card-title">
                <FileText className="me-2" size={20} />
                Teacher's Feedback
              </h5>
              <div className="alert alert-success">
                <p className="mb-0">{assessment.feedback}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Working on Assessment
  if (workingOnAssessment) {
    const assessment = workingOnAssessment;

    return (
      <div className="min-vh-100 bg-light">
        <nav className="navbar navbar-light bg-white border-bottom shadow-sm">
          <div className="container-fluid">
            <button onClick={() => setWorkingOnAssessment(null)} className="btn btn-link text-decoration-none">
              ← Back
            </button>
            <span className="navbar-brand mb-0 h1">{assessment.title}</span>
          </div>
        </nav>

        <div className="container py-4">
          <div className="row">
            <div className="col-lg-8">
              <div className="card shadow mb-4">
                <div className="card-body">
                  <div className="alert alert-info">
                    <strong>Instructions:</strong> {assessment.description}
                  </div>

                  {assessment.questions.map((question, idx) => (
                    <div key={question.id} className="mb-4 pb-4 border-bottom">
                      <h6 className="mb-3">Question {idx + 1}: {question.text}</h6>
                      
                      {question.type === 'essay' && (
                        <textarea
                          className="form-control"
                          rows="8"
                          placeholder="Type your answer here..."
                          value={answers[question.id] || ''}
                          onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        />
                      )}

                      {question.type === 'short_answer' && (
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Type your answer..."
                          value={answers[question.id] || ''}
                          onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        />
                      )}

                      {question.type === 'file_upload' && (
                        <div>
                          <input type="file" className="form-control" />
                          <small className="text-muted">Accepted formats: PDF, JPEG, PNG</small>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="card shadow sticky-top" style={{top: '20px'}}>
                <div className="card-body">
                  <h5 className="card-title">Assessment Info</h5>
                  <div className="mb-3">
                    <small className="text-muted">Class:</small>
                    <p className="fw-bold mb-0">{assessment.className}</p>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted">Due Date:</small>
                    <p className="fw-bold mb-0">{assessment.dueDate}</p>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted">Total Points:</small>
                    <p className="fw-bold mb-0">{assessment.totalPoints}</p>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted">Questions Answered:</small>
                    <p className="fw-bold mb-0">{Object.keys(answers).length} / {assessment.questions.length}</p>
                  </div>
                  <button
                    onClick={submitAssessment}
                    className="btn btn-success w-100"
                    disabled={Object.keys(answers).length !== assessment.questions.length}
                  >
                    <Send className="me-2" size={16} />
                    Submit Assessment
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100 bg-light">
      {/* Header */}
      <nav className="navbar navbar-light bg-white border-bottom shadow-sm">
        <div className="container-fluid">
          <span className="navbar-brand mb-0 h1">
            <BookOpen className="me-2" size={32} style={{color: '#6f42c1'}} />
            BriSchool Student Portal
          </span>
          <div className="d-flex align-items-center">
            <div className="text-end me-3">
              <div className="fw-bold">{DEMO_DATA.currentStudent.firstName} {DEMO_DATA.currentStudent.lastName}</div>
              <small className="text-muted">{DEMO_DATA.currentStudent.yearGroup} • {DEMO_DATA.currentStudent.house} House</small>
            </div>
            <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" 
                 style={{width: '40px', height: '40px', fontWeight: 'bold'}}>
              {DEMO_DATA.currentStudent.firstName[0]}{DEMO_DATA.currentStudent.lastName[0]}
            </div>
          </div>
        </div>
      </nav>

      {/* Navigation */}
      <div className="bg-white border-bottom">
        <div className="container-fluid">
          <ul className="nav nav-pills py-2">
            <li className="nav-item">
              <button onClick={() => setActiveTab('dashboard')} className={`nav-link ${activeTab === 'dashboard' ? 'active' : ''}`}>
                <Home size={16} className="me-2" />Dashboard
              </button>
            </li>
            <li className="nav-item">
              <button onClick={() => setActiveTab('classes')} className={`nav-link ${activeTab === 'classes' ? 'active' : ''}`}>
                <BookOpen size={16} className="me-2" />My Classes
              </button>
            </li>
            <li className="nav-item">
              <button onClick={() => setActiveTab('assessments')} className={`nav-link ${activeTab === 'assessments' ? 'active' : ''}`}>
                <FileText size={16} className="me-2" />Assessments
              </button>
            </li>
            <li className="nav-item">
              <button onClick={() => setActiveTab('grades')} className={`nav-link ${activeTab === 'grades' ? 'active' : ''}`}>
                <Award size={16} className="me-2" />Grades
              </button>
            </li>
            <li className="nav-item">
              <button onClick={() => setActiveTab('schedule')} className={`nav-link ${activeTab === 'schedule' ? 'active' : ''}`}>
                <Calendar size={16} className="me-2" />Schedule
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Main Content */}
      <div className="container-fluid py-4">
        {activeTab === 'dashboard' && (
          <div>
            <h2 className="mb-4">Welcome back, {DEMO_DATA.currentStudent.firstName}!</h2>

            <div className="row g-4 mb-4">
              <div className="col-md-3">
                <div className="card text-white bg-success shadow">
                  <div className="card-body">
                    <TrendingUp size={32} className="mb-2" />
                    <h6 className="opacity-75">Overall Grade</h6>
                    <h2>{DEMO_DATA.academicSummary.overallGrade}%</h2>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card text-white bg-info shadow">
                  <div className="card-body">
                    <CheckCircle size={32} className="mb-2" />
                    <h6 className="opacity-75">Attendance</h6>
                    <h2>{DEMO_DATA.academicSummary.attendance}%</h2>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card text-white bg-primary shadow">
                  <div className="card-body">
                    <Award size={32} className="mb-2" />
                    <h6 className="opacity-75">Class Rank</h6>
                    <h2>#{DEMO_DATA.academicSummary.rank}</h2>
                    <small className="opacity-75">of {DEMO_DATA.academicSummary.totalStudents}</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card text-white bg-warning shadow">
                  <div className="card-body">
                    <Clock size={32} className="mb-2" />
                    <h6 className="opacity-75">Pending Work</h6>
                    <h2>{DEMO_DATA.assessments.filter(a => a.status === 'available').length}</h2>
                    <small className="opacity-75">assignments due</small>
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-4">
              <div className="col-lg-8">
                <div className="card shadow mb-4">
                  <div className="card-header">
                    <h5 className="mb-0">Upcoming Assessments</h5>
                  </div>
                  <div className="list-group list-group-flush">
                    {DEMO_DATA.assessments.filter(a => a.status === 'available').map(assessment => (
                      <div key={assessment.id} className="list-group-item">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="flex-grow-1">
                            <h6 className="mb-1">{assessment.title}</h6>
                            <p className="text-muted mb-0 small">{assessment.className} • Due: {assessment.dueDate}</p>
                          </div>
                          <button
                            onClick={() => setWorkingOnAssessment(assessment)}
                            className="btn btn-primary"
                          >
                            Start
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="card shadow">
                  <div className="card-header">
                    <h5 className="mb-0">Recent Grades</h5>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Assessment</th>
                          <th>Class</th>
                          <th className="text-center">Score</th>
                          <th className="text-center">Grade</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {DEMO_DATA.recentGrades.map((grade, idx) => {
                          const percentage = ((grade.score / grade.maxScore) * 100).toFixed(0);
                          return (
                            <tr key={idx}>
                              <td className="fw-bold">{grade.assessment}</td>
                              <td>{grade.class}</td>
                              <td className="text-center">{grade.score}/{grade.maxScore}</td>
                              <td className="text-center">
                                <span className={`fw-bold ${getGradeColor(grade.score, grade.maxScore)}`}>
                                  {percentage}%
                                </span>
                              </td>
                              <td>{grade.date}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="col-lg-4">
                <div className="card shadow mb-4">
                  <div className="card-header">
                    <h5 className="mb-0">Upcoming Events</h5>
                  </div>
                  <div className="list-group list-group-flush">
                    {DEMO_DATA.upcomingEvents.map(event => (
                      <div key={event.id} className="list-group-item">
                        <div className="d-flex align-items-start">
                          <Calendar size={20} className="text-primary me-2 mt-1" />
                          <div className="flex-grow-1">
                            <h6 className="mb-1">{event.title}</h6>
                            <p className="text-muted mb-0 small">{event.date} • {event.time}</p>
                            <p className="text-muted mb-0 small">{event.location}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'classes' && (
          <div>
            <h2 className="mb-4">My Classes</h2>
            <div className="row g-4">
              {DEMO_DATA.classes.map(cls => (
                <div key={cls.id} className="col-md-6 col-lg-4">
                  <div className="card shadow h-100">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                          <h5 className="card-title mb-1">{cls.name}</h5>
                          <p className="text-muted mb-0">{cls.teacher}</p>
                        </div>
                        <span className={`fs-4 fw-bold ${getGradeColor(cls.currentGrade, 100)}`}>
                          {cls.currentGrade}%
                        </span>
                      </div>
                      <div className="mb-2">
                        <small className="text-muted">Room:</small>
                        <p className="mb-0">{cls.room}</p>
                      </div>
                      <div>
                        <small className="text-muted">Schedule:</small>
                        <p className="mb-0">{cls.schedule}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'assessments' && (
          <div>
            <h2 className="mb-4">Assessments</h2>
            
            <div className="card shadow mb-4">
              <div className="card-header">
                <h5 className="mb-0">Available Assessments</h5>
              </div>
              <div className="list-group list-group-flush">
                {DEMO_DATA.assessments.filter(a => a.status === 'available').map(assessment => (
                  <div key={assessment.id} className="list-group-item">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="flex-grow-1">
                        <h6 className="mb-1">{assessment.title}</h6>
                        <p className="text-muted mb-1 small">{assessment.className}</p>
                        <p className="text-muted mb-0 small">Due: {assessment.dueDate} • {assessment.totalPoints} points</p>
                      </div>
                      <button onClick={() => setWorkingOnAssessment(assessment)} className="btn btn-primary">
                        Start Assessment
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card shadow">
              <div className="card-header">
                <h5 className="mb-0">Submitted Assessments</h5>
              </div>
              <div className="list-group list-group-flush">
                {DEMO_DATA.assessments.filter(a => a.status === 'submitted' || a.status === 'graded').map(assessment => (
                  <div key={assessment.id} className="list-group-item">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="flex-grow-1">
                        <h6 className="mb-1">{assessment.title}</h6>
                        <p className="text-muted mb-1 small">{assessment.className}</p>
                        {assessment.status === 'graded' && (
                          <p className="mb-0">
                            <span className={`fw-bold ${getGradeColor(assessment.score, assessment.totalPoints)}`}>
                              Score: {assessment.score}/{assessment.totalPoints} ({((assessment.score / assessment.totalPoints) * 100).toFixed(0)}%)
                            </span>
                          </p>
                        )}
                      </div>
                      <div className="text-end">
                        <span className={getStatusBadge(assessment.status)}>
                          {assessment.status === 'submitted' ? 'Pending' : 'Graded'}
                        </span>
                        {assessment.status === 'graded' && (
                          <button onClick={() => setViewingGrade(assessment.id)} className="btn btn-success btn-sm mt-2">
                            <Eye size={14} className="me-1" />View
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'grades' && (
          <div>
            <h2 className="mb-4">My Grades</h2>

            <div className="card shadow mb-4">
              <div className="card-body">
                <div className="row text-center">
                  <div className="col-md-4 mb-3">
                    <h6 className="text-muted">Overall Average</h6>
                    <h1 className="text-success">{DEMO_DATA.academicSummary.overallGrade}%</h1>
                  </div>
                  <div className="col-md-4 mb-3">
                    <h6 className="text-muted">Class Rank</h6>
                    <h1 className="text-primary">#{DEMO_DATA.academicSummary.rank}</h1>
                    <small className="text-muted">of {DEMO_DATA.academicSummary.totalStudents}</small>
                  </div>
                  <div className="col-md-4 mb-3">
                    <h6 className="text-muted">Credits</h6>
                    <h1 className="text-info">{DEMO_DATA.academicSummary.creditsEarned}/{DEMO_DATA.academicSummary.creditsRequired}</h1>
                  </div>
                </div>
              </div>
            </div>

            <div className="card shadow">
              <div className="card-header">
                <h5 className="mb-0">Grades by Class</h5>
              </div>
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Class</th>
                      <th>Teacher</th>
                      <th className="text-center">Current Grade</th>
                      <th>Room</th>
                    </tr>
                  </thead>
                  <tbody>
                    {DEMO_DATA.classes.map(cls => (
                      <tr key={cls.id}>
                        <td className="fw-bold">{cls.name}</td>
                        <td>{cls.teacher}</td>
                        <td className="text-center">
                          <span className={`fw-bold fs-5 ${getGradeColor(cls.currentGrade, 100)}`}>
                            {cls.currentGrade}%
                          </span>
                        </td>
                        <td>{cls.room}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'schedule' && (
          <div>
            <h2 className="mb-4">My Schedule</h2>
            <div className="card shadow">
              <div className="card-body">
                {DEMO_DATA.classes.map(cls => (
                  <div key={cls.id} className="card bg-light mb-3">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h5 className="mb-1">{cls.name}</h5>
                          <p className="text-muted mb-0">{cls.teacher} • Room {cls.room}</p>
                        </div>
                        <div className="text-end">
                          <Clock size={20} className="text-primary me-2" />
                          <span className="fw-bold">{cls.schedule}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BriSchoolStudentBootstrap;